import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { addDays, formatDay, formatEatTime, formatIsoDay, parseIsoDay } from '@wodi/domain';
import type { AppointmentView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  DateField,
  EmptyState,
  LoadingBlock,
  MenuButton,
  Notice,
  PageHeader,
  Row,
  Section,
  Select,
  Table,
  TableBody,
  TableHeader,
  Tag,
  useShortcut,
} from '@wodi/ui';
import { useState } from 'react';

import {
  appointmentDayQuery,
  appointmentKeys,
  cancelAppointment,
  markNoShow,
  schedulesQuery,
} from '../api/appointments.ts';
import { can, sessionQuery } from '../api/session.ts';
import { BookingDialog, todayIso, type BookingMode } from '../appointments/booking-dialog.tsx';
import styles from '../appointments/appointments.module.css';
import { KIND_LABELS, reminderText, statusTag } from '../appointments/labels.ts';
import { useOnline } from '../hooks/use-online.ts';
import { outsideDialogs, ShortcutHint, TableScroll } from '../layout/screen.tsx';
import { ReasonDialog } from '../patients/reason-dialog.tsx';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';

// Appointments: one day at a time (docs/plans/phase2-appointments.md §6). The day and the
// schedule are in the address (a date and an internal ID, never patient data).

const route = getRouteApi('/_app/appointments');

function shiftDay(day: string, by: number): string {
  const parsed = parseIsoDay(day);
  return parsed.ok ? formatIsoDay(addDays(parsed.value, by)) : todayIso();
}

function dayInWords(day: string): string {
  const parsed = parseIsoDay(day);
  return parsed.ok ? formatDay(parsed.value) : day;
}

type Pending =
  | { kind: 'cancel'; appointment: AppointmentView }
  | { kind: 'no_show'; appointment: AppointmentView }
  | null;

export function AppointmentsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const search = route.useSearch();
  const navigate = useNavigate();
  const online = useOnline();
  const queryClient = useQueryClient();
  const day = search.day ?? todayIso();
  const scheduleId = search.scheduleId;
  const mayBook = session !== null && can(session, 'appointments.book');
  const mayCheckIn = session !== null && can(session, 'visits.open');
  const schedules = useQuery(schedulesQuery);
  const list = useQuery({ ...appointmentDayQuery(day, scheduleId), refetchInterval: 60_000 });
  const [booking, setBooking] = useState<BookingMode | null>(null);
  const [pending, setPending] = useState<Pending>(null);

  /** `scheduleId`: undefined keeps the current one, null shows all schedules. */
  const go = (next: { day?: string; scheduleId?: string | null }) => {
    const schedule = next.scheduleId === undefined ? scheduleId : (next.scheduleId ?? undefined);
    void navigate({
      to: '/appointments',
      search: { day: next.day ?? day, ...(schedule === undefined ? {} : { scheduleId: schedule }) },
    });
  };

  useShortcut(
    'b',
    outsideDialogs(() => {
      setBooking({ kind: 'book' });
    }),
    { enabled: mayBook && online, description: 'Book an appointment' },
  );
  useShortcut(
    '[',
    outsideDialogs(() => {
      go({ day: shiftDay(day, -1) });
    }),
    { description: 'Previous day' },
  );
  useShortcut(
    ']',
    outsideDialogs(() => {
      go({ day: shiftDay(day, 1) });
    }),
    { description: 'Next day' },
  );

  const end = useMutation({
    mutationFn: ({ p, reason }: { p: NonNullable<Pending>; reason: string }) =>
      p.kind === 'cancel'
        ? cancelAppointment(p.appointment.id, reason)
        : markNoShow(p.appointment.id, reason),
    onSuccess: async (_data, { p }) => {
      await queryClient.invalidateQueries({ queryKey: appointmentKeys.all });
      toasts.show({
        title: p.kind === 'cancel' ? 'Appointment cancelled' : 'Marked as no-show',
      });
      setPending(null);
    },
  });

  const scheduleOptions = [
    { id: 'all', label: 'All schedules' },
    ...(schedules.data?.items ?? []).map((s) => ({ id: s.id, label: s.name })),
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        title="Appointments"
        {...(mayBook
          ? {
              actions: (
                <Button
                  variant="primary"
                  isDisabled={!online}
                  onPress={() => {
                    setBooking({ kind: 'book' });
                  }}
                >
                  Book appointment
                </Button>
              ),
            }
          : {})}
      />
      <div className={styles.toolbar}>
        <Button
          onPress={() => {
            go({ day: shiftDay(day, -1) });
          }}
        >
          Previous day
        </Button>
        <DateField
          label="Day"
          value={day}
          onChange={(v) => {
            if (v !== null) go({ day: v });
          }}
        />
        <Button
          onPress={() => {
            go({ day: shiftDay(day, 1) });
          }}
        >
          Next day
        </Button>
        <Button
          variant="quiet"
          onPress={() => {
            go({ day: todayIso() });
          }}
        >
          Today
        </Button>
        <Select
          label="Schedule"
          options={scheduleOptions}
          value={scheduleId ?? 'all'}
          onChange={(key) => {
            go({ scheduleId: key === 'all' ? null : String(key) });
          }}
        />
      </div>
      {online ? null : (
        <Notice tone="info">
          Offline. Booking, moving and checking in need a connection to Wodi.
        </Notice>
      )}
      <Section title={dayInWords(day)}>
        {list.isPending ? <LoadingBlock label="Loading appointments" rows={4} /> : null}
        {list.isError ? (
          <Notice
            tone="error"
            action={
              <Button dense onPress={() => void list.refetch()}>
                Try again
              </Button>
            }
          >
            {errorText(list.error, "The appointments couldn't be loaded. Try again.")}
          </Notice>
        ) : null}
        {list.isSuccess && list.data.items.length === 0 ? (
          <EmptyState>
            {`No appointments on ${dayInWords(day)}.${mayBook ? ' Book one with Book appointment.' : ''}`}
          </EmptyState>
        ) : null}
        {list.isSuccess && list.data.items.length > 0 ? (
          <TableScroll>
            <Table aria-label={`Appointments on ${dayInWords(day)}`} flat>
              <TableHeader>
                <Column>Time</Column>
                <Column isRowHeader>Patient</Column>
                <Column>Clinic</Column>
                <Column>Kind</Column>
                <Column>Status</Column>
                <Column>SMS</Column>
                <Column>
                  <span className={styles.meta}>Action</span>
                </Column>
              </TableHeader>
              <TableBody items={list.data.items}>
                {(a) => {
                  const tag = statusTag(a);
                  // As of the last load; the list reloads every minute.
                  const started = new Date(a.startsAt).getTime() <= list.dataUpdatedAt;
                  const menu = [
                    { id: 'move', label: 'Move' },
                    { id: 'cancel', label: 'Cancel appointment' },
                    ...(started ? [{ id: 'no_show', label: 'Mark as no-show' }] : []),
                  ];
                  return (
                    <Row id={a.id}>
                      <Cell>
                        <span className={styles.time}>{formatEatTime(new Date(a.startsAt))}</span>
                      </Cell>
                      <Cell>
                        <Link to="/patients/$patientId" params={{ patientId: a.patient.id }}>
                          {a.patient.displayName}
                        </Link>{' '}
                        <span className="wodi-id">{a.patient.patientNumber}</span>
                      </Cell>
                      <Cell>
                        {a.servicePoint.name}
                        {a.practitioner === null ? null : (
                          <span className={styles.meta}>{` ${a.practitioner.name}`}</span>
                        )}
                      </Cell>
                      <Cell>{KIND_LABELS[a.kind]}</Cell>
                      <Cell>
                        <Tag state={tag.state}>{tag.label}</Tag>
                      </Cell>
                      <Cell>
                        <span className={styles.meta}>{reminderText(a.reminder)}</span>
                      </Cell>
                      <Cell>
                        {a.status === 'booked' ? (
                          <span className={styles.actions}>
                            {mayCheckIn && day === todayIso() && online ? (
                              <Link
                                to="/reception"
                                search={{ patientId: a.patient.id, appointmentId: a.id }}
                              >
                                Check in
                              </Link>
                            ) : null}
                            {mayBook && online ? (
                              <MenuButton
                                label="More"
                                items={menu}
                                onAction={(id) => {
                                  if (id === 'move') {
                                    setBooking({ kind: 'reschedule', appointment: a });
                                  } else {
                                    end.reset();
                                    setPending({
                                      kind: id === 'cancel' ? 'cancel' : 'no_show',
                                      appointment: a,
                                    });
                                  }
                                }}
                              />
                            ) : null}
                          </span>
                        ) : null}
                      </Cell>
                    </Row>
                  );
                }}
              </TableBody>
            </Table>
          </TableScroll>
        ) : null}
        {mayBook ? <ShortcutHint combo="b" action="book an appointment" /> : null}
        <ShortcutHint combo="[" to="]" action="move a day back or forward" />
      </Section>
      {booking === null ? null : (
        <BookingDialog
          mode={booking}
          initialDay={day < todayIso() ? todayIso() : day}
          isOpen
          onOpenChange={(open) => {
            if (!open) setBooking(null);
          }}
          onDone={(appointment) => {
            toasts.show({
              title: booking.kind === 'reschedule' ? 'Appointment moved' : 'Appointment booked',
              description: `${appointment.patient.displayName}, ${formatEatTime(new Date(appointment.startsAt))}.`,
            });
            setBooking(null);
          }}
        />
      )}
      {pending === null ? null : (
        <ReasonDialog
          title={pending.kind === 'cancel' ? 'Cancel appointment' : 'Mark as no-show'}
          isOpen
          onOpenChange={(open) => {
            if (!open) setPending(null);
          }}
          confirmLabel={pending.kind === 'cancel' ? 'Cancel appointment' : 'Mark as no-show'}
          pendingLabel="Saving…"
          isPending={end.isPending}
          error={end.isError ? errorText(end.error, "That couldn't be saved. Try again.") : null}
          onConfirm={(reason) => {
            end.mutate({ p: pending, reason });
          }}
        >
          <p>{`${pending.appointment.patient.displayName}, ${formatEatTime(new Date(pending.appointment.startsAt))}.`}</p>
        </ReasonDialog>
      )}
    </div>
  );
}
